import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Paper } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import neuroService from '../services/neuro.service';

const COLORS = ['#784e3c', '#c4a088', '#5a7a9a'];

const EstablishmentDashboardPage = () => {
  const [establishments, setEstablishments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionName, setSessionName] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    neuroService.getMyEstablishments()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setEstablishments(list);
        if (list.length === 1) setSelected(list[0]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selected?.id) return;
    neuroService.getEstablishmentStats(selected.id).then(setStats);
    neuroService.getClassSessions(selected.id).then((data) => setSessions(Array.isArray(data) ? data : []));
  }, [selected?.id]);

  const handleCreateSession = async () => {
    if (!selected?.id || !sessionName) return;
    await neuroService.createClassSession(selected.id, sessionName);
    setSessionName('');
    const data = await neuroService.getClassSessions(selected.id);
    setSessions(Array.isArray(data) ? data : []);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#784e3c' }} />
      </Box>
    );
  }

  const pieData = stats?.distribution ? [
    { name: 'Attention élevée', value: stats.distribution.high_attention_pct || 0 },
    { name: 'Sensoriel élevé', value: stats.distribution.high_sensory_pct || 0 },
    { name: 'Apprentissage élevé', value: stats.distribution.high_learning_pct || 0 },
  ] : [];

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FFF9F0', p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" sx={{ color: '#784e3c', fontWeight: 700, mb: 3 }}>
        Tableau de bord établissement
      </Typography>

      {establishments.length > 1 && (
        <select
          value={selected?.id || ''}
          onChange={(e) => setSelected(establishments.find((x) => x.id === parseInt(e.target.value, 10)))}
          style={{ marginBottom: 24, padding: 8, borderRadius: 8, border: '1px solid #ccc', width: '100%' }}
        >
          <option value="">Choisir un établissement</option>
          {establishments.map((e) => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>
      )}

      {selected && stats && (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>{selected.name} — statistiques anonymisées</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {stats.total_evaluations} évaluation(s) complétée(s) — aucun nom affiché
            </Typography>
            {stats.averages && (
              <Typography variant="body2" sx={{ mb: 2 }}>
                Moyennes : Dynamisme {stats.averages.tdah}/10 — Sensoriel {stats.averages.tsa}/10 — Apprentissage {stats.averages.dys}/10
              </Typography>
            )}
            {pieData.some((d) => d.value > 0) && (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Sessions classe</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <input
                placeholder="Nom de la classe"
                value={sessionName}
                onChange={(e) => setSessionName(e.target.value)}
                style={{ flex: 1, padding: 10, borderRadius: 8, border: '1px solid #ccc' }}
              />
              <Button variant="contained" onClick={handleCreateSession} sx={{ backgroundColor: '#784e3c' }}>
                Créer
              </Button>
            </Box>
            {sessions.map((s) => (
              <Box key={s.id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                <Typography>{s.name}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', px: 1, borderRadius: 1 }}>
                  Code : {s.session_code}
                </Typography>
              </Box>
            ))}
          </Paper>
        </>
      )}

      {!establishments.length && (
        <Typography color="text.secondary">Aucun établissement associé à votre compte.</Typography>
      )}
    </Box>
  );
};

export default EstablishmentDashboardPage;
